import React from "react";
import {
  useNavigate,
  Link,
  useLocation,
  Routes,
  Route,
} from "react-router-dom";
import { useAuth } from "@/features/auth/context/AuthContext";
import { Button } from "@/components";
import {
  LayoutDashboard,
  Menu,
  LogOut,
  Settings,
  Layers,
  Users,
  Bell,
  ChevronLeft,
  LinkIcon,
  ChevronRight,
  Megaphone,
  Share2,
  Code,
  UserCircle,
  Shield,
  BotIcon,
  Wallet,
  Wand,
  LucideGlasses,
} from "lucide-react";
import toast from "react-hot-toast";
import logo from "@/assets/images/logo.png";
import {
  ContentWizard,
  Campaigns,
  Agents,
  Branding,
  Platforms,
  IntegrationsDeveloper,
  Team,
  TeamMembers,
  TeamRoles,
  BillingUsage,
  Settings as SettingsPage,
  ContentReview,
} from "./pages";
import { NotFound } from "@/pages/NotFound";
import {
  ProSidebarProvider,
  Sidebar,
  Menu as ProMenu,
  MenuItem,
  SubMenu,
  useProSidebar,
} from "react-pro-sidebar";

// Menu items with nested support
const menuItems = [
  {
    icon: LayoutDashboard,
    label: "Content",
    path: "/home/content-wizard",
    children: [
      {
        icon: Wand,
        label: "Content Wizard",
        path: "/home/content-wizard",
      },
      {
        icon: LucideGlasses,
        label: "Review",
        path: "/home/content-review",
      },
    ],
  },
  {
    icon: Megaphone,
    label: "Campaigns",
    path: "/home/campaigns",
    children: null,
  },
  {
    icon: BotIcon,
    label: "Agents",
    path: "/home/agents",
    children: null,
  },
  {
    icon: Layers,
    label: "Branding",
    path: "/home/branding",
    children: null,
  },
  {
    icon: LinkIcon,
    label: "Integrations",
    path: "/home/integrations",
    children: [
      {
        icon: Share2,
        label: "Platforms",
        path: "/home/integrations/platforms",
      },
      {
        icon: Code,
        label: "Developer",
        path: "/home/integrations/developer",
      },
    ],
  },
  {
    icon: Users,
    label: "Team",
    path: "/home/team",
    children: [
      {
        icon: UserCircle,
        label: "Members",
        path: "/home/team/members",
      },
      {
        icon: Shield,
        label: "Roles",
        path: "/home/team/roles",
      },
    ],
  },
  {
    icon: Wallet,
    label: "Billing & Usage",
    path: "/home/billing-usage",
    children: null,
  },
  {
    icon: Settings,
    label: "Settings",
    path: "/home/settings",
    children: null,
  },
];

const DashboardContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { collapseSidebar, collapsed, toggleSidebar, broken } = useProSidebar();

  const handleLogout = async () => {
    try {
      await logout();
      toast.success("Logged out successfully");
      navigate("/login");
    } catch (error) {
      toast.error("Failed to logout");
    }
  };

  const isActive = (path: string) => {
    if (path === "/home/content-wizard") {
      return (
        location.pathname === path ||
        location.pathname === "/home/content-wizard"
      );
    }
    return location.pathname.startsWith(path);
  };

  const renderMenuItems = () => {
    return menuItems.map((item) => {
      const Icon = item.icon;
      const active = isActive(item.path);

      if (item.children && item.children.length > 0) {
        return (
          <SubMenu
            key={item.path}
            label={item.label}
            icon={<Icon className="h-5 w-5" />}
            defaultOpen={active}
          >
            {item.children.map((child) => {
              const ChildIcon = child.icon;
              return (
                <MenuItem
                  key={child.path}
                  active={location.pathname === child.path}
                  icon={
                    ChildIcon ? <ChildIcon className="h-4 w-4" /> : undefined
                  }
                  onClick={() => {
                    navigate(child.path);
                    if (broken) {
                      toggleSidebar();
                    }
                  }}
                >
                  {child.label}
                </MenuItem>
              );
            })}
          </SubMenu>
        );
      }

      return (
        <MenuItem
          key={item.path}
          icon={<Icon className="h-5 w-5" />}
          active={active}
          onClick={() => {
            navigate(item.path);
            if (broken) {
              toggleSidebar();
            }
          }}
        >
          {item.label}
        </MenuItem>
      );
    });
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 border-b border-input bg-card pt-[env(safe-area-inset-top)]">
        <Link to="/home/content-wizard" className="inline-block shrink-0">
          <img
            src={logo}
            alt="Postpally"
            className="h-6 sm:h-7 w-auto cursor-pointer hover:opacity-80 transition-opacity"
          />
        </Link>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => toggleSidebar()}
          className="h-10 w-10 shrink-0 touch-manipulation"
          aria-label="Toggle menu"
        >
          <Menu className="h-5 w-5 sm:h-6 sm:w-6" />
        </Button>
      </header>

      {/* Sidebar */}
      <Sidebar
        collapsed={collapsed}
        breakPoint="lg"
        width={collapsed ? "80px" : "256px"}
        style={{
          height: "100vh",
          position: "fixed",
          zIndex: 40,
        }}
        rootStyles={{
          ...(broken && {
            backgroundColor: "rgba(255, 255, 255, 0.88)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
          }),
        }}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-input flex items-center justify-between">
            <Link
              to="/home/content-wizard"
              className={`inline-flex items-center gap-3 ${
                collapsed ? "justify-center" : ""
              }`}
            >
              <img
                src={logo}
                alt="Postpally"
                className="h-8 w-auto cursor-pointer hover:opacity-80 transition-opacity"
              />
              {!collapsed && (
                <span className="text-xl font-bold">Postpally</span>
              )}
            </Link>
            {!collapsed && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => collapseSidebar()}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}
          </div>
          {collapsed && (
            <div className="px-2 pb-2 border-b border-input flex justify-center">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => collapseSidebar()}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Navigation */}
          <div className="flex-1 overflow-y-auto">
            <ProMenu
              menuItemStyles={{
                button: {
                  fontSize: "0.875rem",
                  [`&.ps-active`]: {
                    backgroundColor: "#5E46BF",
                    color: "white",
                  },
                  [`&.ps-active .ps-menu-icon`]: {
                    color: "white",
                  },
                  [`&.ps-active .ps-menu-label`]: {
                    color: "white",
                  },
                  [`&:hover`]: {
                    backgroundColor: "rgba(94, 70, 191, 0.2)",
                    color: "hsl(var(--foreground))",
                  },
                  [`&:hover .ps-menu-label`]: {
                    color: "#000000",
                  },
                },
                subMenuContent: {
                  backgroundColor: "transparent",
                  paddingLeft: "2rem",
                  [`& .ps-menu-button`]: {
                    gap: "0.75rem",
                    paddingLeft: "0.5rem",
                    fontSize: "0.875rem",
                  },
                  [`& .ps-menu-button:hover .ps-menu-label`]: {
                    color: "#000000",
                  },
                  [`& .ps-menu-icon`]: {
                    marginRight: "0",
                    marginLeft: "0",
                    width: "1rem",
                    minWidth: "1rem",
                  },
                },
              }}
            >
              {renderMenuItems()}
            </ProMenu>
          </div>

          {/* User Section */}
          <div className="p-4 border-t border-input space-y-3">
            {!collapsed && (
              <div className="flex items-center gap-3 px-4 py-2">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-primary font-semibold">
                    {user?.firstName?.[0] || "U"}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {user?.email}
                  </p>
                </div>
              </div>
            )}
            {collapsed && (
              <div className="flex justify-center">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-primary font-semibold">
                    {user?.firstName?.[0] || "U"}
                  </span>
                </div>
              </div>
            )}
            <Button
              variant="ghost"
              className={`w-full ${
                collapsed ? "justify-center" : "justify-start"
              }`}
              onClick={handleLogout}
              title={collapsed ? "Logout" : undefined}
            >
              <LogOut className="h-4 w-4" />
              {!collapsed && <span className="ml-2">Logout</span>}
            </Button>
          </div>
        </div>
      </Sidebar>

      {/* Main Content */}
      <main
        className="flex-1 min-w-0 transition-all duration-300 lg:ml-0"
        style={{
          marginLeft: broken ? "0" : collapsed ? "80px" : "256px",
        }}
      >
        {/* Top Bar */}
        <header className="sticky top-0 z-20 bg-card border-b border-input lg:mt-0 mt-14 sm:mt-16">
          <div className="flex items-center justify-end px-4 py-3 sm:p-4">
            {" "}
            {/* Changed to justify-end */}
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="h-5 w-5" />
              <span className="absolute top-1 right-1 h-2 w-2 bg-destructive rounded-full"></span>
            </Button>
          </div>
        </header>

        {/* Dashboard Content */}
        <Routes>
          <Route index element={<ContentWizard />} />
          <Route path="content-review" element={<ContentReview />} />
          <Route path="content-wizard" element={<ContentWizard />} />
          <Route path="campaigns" element={<Campaigns />} />
          <Route path="agents" element={<Agents />} />
          <Route path="branding" element={<Branding />} />
          <Route path="integrations/platforms" element={<Platforms />} />
          <Route
            path="integrations/developer"
            element={<IntegrationsDeveloper />}
          />
          <Route path="team" element={<Team />} />
          <Route path="team/members" element={<TeamMembers />} />
          <Route path="team/roles" element={<TeamRoles />} />
          <Route path="billing-usage" element={<BillingUsage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
    </div>
  );
};

export const Dashboard: React.FC = () => {
  return (
    <ProSidebarProvider>
      <DashboardContent />
    </ProSidebarProvider>
  );
};
